import { isObjEmpty, msToSecs, isArray, clone } from '@helpers/utils.js'
import { TEAM_MEMBER_LEVEL_ADMINISTRATOR } from '@config/options.js'
import { LS_TEAMS } from '@config/localstorage.js'
import { useLocalStorage } from '@vueuse/core'
import { GAME_ID } from '@config/game.js'
import { computed } from 'vue'

const teamState = useLocalStorage(LS_TEAMS, null)

export default function () {
  const myTeams = computed(() => {
    const teams = JSON.parse(teamState.value)
    return isArray(teams) ? teams : []
  })

  const myMods = computed(() =>
    myTeams.value.filter(
      (t) =>
        t.resource_type === 'mod' &&
        t.game_id === GAME_ID &&
        t.access.invite_pending === 0
    )
  )

  function setMyTeams(teams) {
    teamState.value = JSON.stringify(teams)
  }

  function clearMyTeamsStore() {
    teamState.value = null
  }

  function addTeamToMyTeams(team) {
    if (
      (!team &&
        !team.resource_type &&
        !team.resource_id &&
        !team.access &&
        !team.access.level) ||
      !myTeams.value
    ) {
      return
    }

    const temp = clone(myTeams.value)
    const index = temp.findIndex((t) =>
      _findTeam(t, { resource: team.resource_type, id: team.resource_id })
    )

    if (index !== -1) {
      temp.splice(index, 1, team)
    } else {
      temp.push(team)
    }

    setMyTeams(temp)
  }

  function updateModTeam(mod, access) {
    if (mod && !isObjEmpty(mod)) {
      const team = findTeam({ resource: 'mod', id: mod.id })

      let updated = _createModTeam(mod)

      if (team) {
        updated = {
          ...team,
          ...updated,
          access: access || team.access,
        }
      } else if (access) {
        updated.access = access
      }

      addTeamToMyTeams(updated)
    }
  }

  function _createModTeam(mod) {
    const temp = clone(mod)
    return {
      access: {
        position: '',
        is_leader: false,
        invite_pending: 0,
        date_added: msToSecs(new Date().getTime()),
        level: TEAM_MEMBER_LEVEL_ADMINISTRATOR,
      },
      logo: temp.logo,
      name: temp.name,
      name_id: temp.name_id,
      game_name: temp.game_name,
      game_curation_option: temp.curation_option,
      profile_url: temp.profile_url,
      visible: temp.visible,
      status: temp.status,
      resource_id: temp.id,
      resource_type: 'mod',
      monetization: temp.monetization,
      monetization_options: temp.monetization_options,
    }
  }

  function addTeamLeader(resourceType, id, nameId) {
    if (!resourceType || !id || !nameId) {
      throw new TypeError()
    }

    const team = findTeam({ resource: resourceType, id })
    if (!team) {
      throw new Error(`Unable to add ${resourceType} team leader`)
    }

    const defaultTeamLeaderPermission = {
      name_id: nameId,
      resource_id: id,
      resource_type: resourceType,
      access: {
        level: TEAM_MEMBER_LEVEL_ADMINISTRATOR,
        position: '',
        is_leader: true,
        date_added: msToSecs(new Date().getTime()),
        invite_pending: 0,
      },
    }

    addTeamToMyTeams({ ...team, ...defaultTeamLeaderPermission })
  }

  function findTeam({ resource, id }) {
    if (!resource || !id) return null

    return (
      myTeams.value &&
      isArray(myTeams.value) &&
      myTeams.value.find((t) => _findTeam(t, { resource, id }))
    )
  }

  function _findTeam(t, { resource, id }) {
    return t.resource_type === resource && t.resource_id === id
  }

  function removeTeam({ resource, id }) {
    if (!resource || !id) return

    const index = myTeams.value.findIndex((t) => _findTeam(t, { resource, id }))
    if (index !== -1) {
      const temp = clone(myTeams.value)
      temp.splice(index, 1)
      setMyTeams(temp)
    }
  }

  return {
    clearMyTeamsStore,
    updateModTeam,
    addTeamLeader,
    setMyTeams,
    removeTeam,
    myTeams,
    myMods,
  }
}
